import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FolderInput } from 'lucide-react';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface UncategorizedVendor {
  id: string;
  businessName: string;
}

/**
 * Vendors whose `primaryCategory` stayed null after import (rolodex-import
 * plan, Task 12). Guessing a category from the business name was rejected in
 * the design spec — "Sally Blue Florals" is probably florals, which is right
 * often enough to be trusted and wrong quietly. This is what replaces the
 * guess: the planner sees exactly which vendors need a category and assigns
 * it herself, in bulk rather than one detail page at a time.
 *
 * Renders nothing when there is nothing uncategorized — this panel is a
 * cleanup task, not a permanent fixture of the page.
 */
export default function UncategorizedPanel({
  vendors,
  categories,
}: {
  vendors: UncategorizedVendor[];
  categories: string[];
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [category, setCategory] = useState('');

  const allSelected = vendors.length > 0 && selected.size === vendors.length;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(vendors.map((v) => v.id)));
  };

  const assign = useMutation({
    mutationFn: async () => {
      const res = await client.api.vendors['bulk-category'].$post({
        json: { vendorIds: [...selected], primaryCategory: category },
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? 'Failed to assign category');
      }
      return res.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      toast.success(
        `Assigned ${result.updated} vendor${result.updated === 1 ? '' : 's'} to ${category}`,
      );
      setSelected(new Set());
      setCategory('');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (vendors.length === 0) return null;

  return (
    <Card className="rounded-3xl bg-card p-5 shadow-ambient">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="display-tight font-semibold">Uncategorized ({vendors.length})</p>
          <p className="text-xs text-muted-foreground">
            Imported without a category. Pick one and assign it to as many as you want at once.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-48" aria-label="Category to assign">
              <SelectValue placeholder="Choose category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            disabled={selected.size === 0 || !category || assign.isPending}
            onClick={() => assign.mutate()}
          >
            <FolderInput className="size-4" />
            {assign.isPending
              ? 'Assigning…'
              : `Assign ${selected.size > 0 ? `(${selected.size})` : ''}`}
          </Button>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 border-b pb-2">
        <Checkbox
          id="uncategorized-select-all"
          checked={allSelected}
          onCheckedChange={toggleAll}
          aria-label="Select all uncategorized vendors"
        />
        <label htmlFor="uncategorized-select-all" className="text-xs text-muted-foreground">
          Select all
        </label>
      </div>

      <ScrollArea className="mt-2 max-h-64">
        <div className="space-y-1">
          {vendors.map((v) => (
            <label
              key={v.id}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-[var(--color-surface-container-high)]"
            >
              <Checkbox
                checked={selected.has(v.id)}
                onCheckedChange={() => toggle(v.id)}
                aria-label={`Select ${v.businessName}`}
              />
              {v.businessName}
            </label>
          ))}
        </div>
      </ScrollArea>
    </Card>
  );
}
